// src/pages/business/BusinessSelectionPage.tsx
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, CheckCircle, XCircle,
  Plus, RefreshCw, WifiOff, Loader2, Rocket, Lock,
  ArrowUpCircle, Clock, Home, Tag,
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { PortalCard } from '@/components/cards/PortalCard';
import { useToast } from '@/contexts/ToastContext';
import { supabase } from '@/config/supabase';

interface Business {
  id: string;
  name: string;
  category: string;
  logo_url?: string;
  is_active?: boolean;
}

interface SubscriptionLimits {
  subscribed: boolean;
  can_add_more: boolean;
  max_allowed: number;
  verification_pending: boolean;
  plan?: { name: string };
}

export const BusinessSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile, setActiveRole } = useAuth();
  const { warning, error: toastError } = useToast();
  const [switchingRole, setSwitchingRole] = useState(false);

  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limits, setLimits] = useState<SubscriptionLimits>({
    subscribed: true, can_add_more: true, max_allowed: 5,
    verification_pending: false,
  });

  const loadBusinesses = useCallback(async () => {
    try {
      setError(null);
      const [bizRes, limitsRes] = await Promise.allSettled([
        apiClient.get<Business[]>('/businesses'),
        apiClient.get('/subscriptions/my-limits'),
      ]);

      if (bizRes.status === 'fulfilled') {
        setBusinesses(bizRes.value.data || []);
      } else {
        setError('Failed to load businesses. Please try again.');
        toastError('Failed to load', 'Could not fetch your businesses. Please try again.');
      }

      if (limitsRes.status === 'fulfilled') {
        const d = limitsRes.value.data?.business;
        setLimits({
          subscribed: d?.subscribed ?? true,
          can_add_more: d?.can_add_more ?? true,
          max_allowed: d?.max_allowed ?? 5,
          verification_pending: d?.verification_pending ?? false,
          plan: d?.plan,
        });
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load businesses.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadBusinesses(); }, [loadBusinesses]);

  // Auto-route on initial launch if they only have 1 business (or based on last visited)
  useEffect(() => {
    if (loading || businesses.length === 0) return;
    
    // Only auto-route once per session to allow them to come back and add another business
    if (sessionStorage.getItem('majengo_has_auto_routed')) return;
    sessionStorage.setItem('majengo_has_auto_routed', 'true');

    // If they only have 1 business, auto-launch it!
    if (businesses.length === 1) {
      handleSelect(businesses[0]);
    }
  }, [loading, businesses]);

  const handleRefresh = () => { setRefreshing(true); loadBusinesses(); };

  const handleRegister = () => {
    if (!limits.subscribed) {
      warning('Subscription Required', 'You need a Business subscription to register businesses.');
      return;
    }
    if (!limits.can_add_more) {
      warning('Business Limit Reached', `Your ${limits.plan?.name ?? 'current'} plan allows ${limits.max_allowed} business${limits.max_allowed === 1 ? '' : 'es'}. Upgrade to add more.`);
      return;
    }
    navigate('/business/register');
  };

  const handleSelect = (biz: Business) => {
    if (!limits.subscribed) {
      warning('Subscription Required', 'Subscribe to a Business plan to manage businesses.');
      return;
    }
    
    // Check user preference for startup screen
    const startupPref = localStorage.getItem('majengo_startup_screen');
    if (startupPref === 'pos') {
      navigate(`/business/${biz.id}/products`);
    } else {
      navigate(`/business/${biz.id}`);
    }
  };

  const handleGoPersonal = () => {
    navigate('/');
  };

  return (
    <div className="flex flex-col min-h-screen w-full bg-light-bg dark:bg-dark-bg">

      {/* ── Sticky header — mirrors LandlordLayout header exactly ── */}
      <header className="sticky top-0 z-50 w-full border-b border-light-border dark:border-dark-border bg-light-card/70 dark:bg-dark-card/80 backdrop-blur-md">
        <div className="mx-auto flex items-center justify-between gap-4 px-4 py-[6px] md:px-8">
          {/* Left: portal title */}
          <button
            type="button"
            onClick={() => navigate('/business')}
            className="flex items-center gap-2 text-sm font-semibold text-light-text dark:text-dark-text hover:text-primary dark:hover:text-golden"
          >
            <Building2 className="h-5 w-5" />
            Business Portal
          </button>

          {/* Right: actions */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleGoPersonal}
              className="inline-flex items-center gap-2 rounded-lg border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg px-3 py-2 text-sm font-medium text-light-text dark:text-dark-text hover:bg-light-card dark:hover:bg-dark-card transition-colors"
            >
              <Home className="h-4 w-4" />
              Personal
            </button>

            <button
              type="button"
              onClick={handleRegister}
              title="Register business"
              className="inline-flex items-center justify-center rounded-lg border border-light-border dark:border-dark-border bg-light-bg dark:bg-dark-bg p-2 text-light-text dark:text-dark-text hover:bg-light-card dark:hover:bg-dark-card transition-colors"
            >
              <Plus className="h-4 w-4" />
            </button>

            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* ── Main content — mirrors LandlordLayout main exactly ── */}
      <main className="flex-1 overflow-y-auto w-full">
        <div className="p-6 md:p-10 pt-[24px] md:pt-[24px] w-full max-w-5xl mx-auto min-h-[calc(100vh-57px)]">

          {/* ── Subscription banners — retained at top ── */}
          {(!limits.subscribed || limits.verification_pending || !limits.can_add_more) && (
            <div className="space-y-2 mb-6">
              {!limits.subscribed && (
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full flex items-center gap-2 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 text-left"
                >
                  <Lock size={15} className="text-amber-500 flex-shrink-0" />
                  <span className="text-xs text-amber-800 dark:text-amber-300 flex-1 leading-snug">
                    Subscribe to a Business plan to register businesses and unlock features →
                  </span>
                </button>
              )}

              {limits.subscribed && !loading && businesses.length === 0 && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-primary/10 border-l-4 border-primary">
                  <Rocket size={15} className="text-primary flex-shrink-0" />
                  <span className="text-xs text-primary flex-1 leading-snug">
                    🎉 Your Business subscription is active! Register your first business to get started.
                  </span>
                </div>
              )}

              {limits.subscribed && limits.verification_pending && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-primary/10 border-l-4 border-primary">
                  <Clock size={15} className="text-primary flex-shrink-0" />
                  <span className="text-xs text-primary flex-1 leading-snug">
                    Verification pending — staff management, AI, and public product listing unlock once approved.
                  </span>
                </div>
              )}

              {limits.subscribed && !limits.can_add_more && (
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full flex items-center gap-2 p-3 rounded-xl bg-sky-50 dark:bg-sky-900/20 border-l-4 border-sky-500 text-left"
                >
                  <ArrowUpCircle size={15} className="text-sky-500 flex-shrink-0" />
                  <span className="text-xs text-sky-700 dark:text-sky-300 flex-1 leading-snug">
                    Business limit reached ({limits.max_allowed}). Upgrade to add more →
                  </span>
                </button>
              )}
            </div>
          )}

          {/* ── Label row — mirrors PropertyList label row exactly ── */}
          <div className="flex items-center justify-between px-2 pt-0 pb-4">
            <p className="text-xs font-semibold tracking-wide text-light-subtle dark:text-dark-subtle uppercase">
              All Businesses
            </p>
            <div className="flex items-center gap-3">
              {/* Plan badge */}
              <div className="flex items-center gap-2 bg-light-card/80 dark:bg-dark-card/80 px-4 py-1.5 rounded-full">
                <span className="text-xs font-medium text-light-text dark:text-dark-text">Plan:</span>
                <span className="text-xs font-semibold text-primary">
                  {limits.plan?.name ?? (limits.subscribed ? 'Active' : 'Free')}
                </span>
              </div>
              {/* Refresh */}
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-light-subtle dark:text-dark-subtle hover:text-primary transition-colors"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* ── Loading ── */}
          {loading && businesses.length === 0 && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-primary" size={32} />
            </div>
          )}

          {/* ── Error ── */}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <WifiOff size={48} className="text-light-subtle dark:text-dark-subtle" />
              <p className="text-sm text-light-subtle dark:text-dark-subtle">{error}</p>
              <button
                onClick={() => { setError(null); loadBusinesses(); }}
                className="px-5 py-2 rounded-xl bg-primary text-white text-sm font-semibold"
              >
                Retry
              </button>
            </div>
          )}

          {/* ── Card grid — mirrors PropertyList grid exactly ── */}
          {!loading && !error && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {businesses.length === 0 ? (
                <div className="col-span-full py-12 text-center bg-light-card dark:bg-dark-card rounded-xl border border-dashed border-light-border dark:border-dark-border">
                  <p className="text-lg font-medium text-light-text dark:text-dark-text">No businesses yet</p>
                  <p className="text-sm text-light-subtle dark:text-dark-subtle mt-2">
                    Start by registering one using the "+" button in the top bar.
                  </p>
                </div>
              ) : (
                businesses.map(biz => (
                  <PortalCard
                    key={biz.id}
                    title={biz.name}
                    subtitle={biz.category}
                    icon={Building2}
                    iconBg="bg-light-bg dark:bg-dark-bg"
                    iconColor="text-light-subtle dark:text-dark-subtle"
                    badge={
                      <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                        biz.is_active !== false
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-500 dark:text-red-400'
                      }`}>
                        {biz.is_active !== false
                          ? <CheckCircle size={11} />
                          : <XCircle size={11} />
                        }
                        {biz.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    }
                    stats={[
                      { icon: Tag, label: biz.category },
                    ]}
                    onClick={() => handleSelect(biz)}
                  />
                ))
              )}
            </div>
          )}

        </div>
      </main>

      {/* ── FAB — register business ── */}
      {businesses.length > 0 && (
        <button
          onClick={handleRegister}
          className="fixed bottom-8 right-6 w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center shadow-xl hover:scale-105 transition-transform z-50"
        >
          <Plus size={28} />
        </button>
      )}
    </div>
  );
};

export default BusinessSelectionPage;
